/**
 * Window Manager Context
 * Manages multiple floating incident windows (like Microsoft 365)
 */

import { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import type { Incident } from '@/types/incidents';

export interface WindowState {
  id: string; // Window ID (unique)
  incidentId: string;
  incident: Incident;
  position: { x: number; y: number };
  size: { width: number; height: number };
  zIndex: number;
  isMinimized: boolean;
  isFocused: boolean;
}

interface WindowManagerContextValue {
  windows: WindowState[];
  openWindow: (incident: Incident) => void;
  closeWindow: (windowId: string) => void;
  focusWindow: (windowId: string) => void;
  minimizeWindow: (windowId: string) => void;
  restoreWindow: (windowId: string) => void;
  updateWindowPosition: (windowId: string, position: { x: number; y: number }) => void;
  updateWindowSize: (windowId: string, size: { width: number; height: number }) => void;
  closeAllWindows: () => void;
}

const WindowManagerContext = createContext<WindowManagerContextValue | undefined>(undefined);

const DEFAULT_WINDOW_SIZE = { width: 800, height: 600 };
const WINDOW_OFFSET = 40; // Offset for cascading windows
const MAX_WINDOWS = 5; // Prevent too many windows

export function WindowManagerProvider({ children }: { children: ReactNode }) {
  const [windows, setWindows] = useState<WindowState[]>([]);
  const [nextZIndex, setNextZIndex] = useState(1000);

  /**
   * Calculate position for new window (cascade style)
   */
  const calculateNewWindowPosition = useCallback((): { x: number; y: number } => {
    const offset = windows.length * WINDOW_OFFSET;
    const baseX = 100;
    const baseY = 100;

    return {
      x: baseX + offset,
      y: baseY + offset,
    };
  }, [windows.length]);

  /**
   * Open a new window or focus existing one
   */
  const openWindow = useCallback(
    (incident: Incident) => {
      // Check if window already exists for this incident
      const existingWindow = windows.find((w) => w.incidentId === incident.id);

      if (existingWindow) {
        // Focus existing window
        focusWindow(existingWindow.id);
        return;
      }

      // Check max windows limit
      if (windows.length >= MAX_WINDOWS) {
        console.warn(`Maximum ${MAX_WINDOWS} windows reached`);
        return;
      }

      // Create new window
      const newWindow: WindowState = {
        id: `window-${Date.now()}-${Math.random()}`,
        incidentId: incident.id,
        incident,
        position: calculateNewWindowPosition(),
        size: DEFAULT_WINDOW_SIZE,
        zIndex: nextZIndex,
        isMinimized: false,
        isFocused: true,
      };

      setWindows((prev) => [
        ...prev.map((w) => ({ ...w, isFocused: false })),
        newWindow,
      ]);
      setNextZIndex((z) => z + 1);
    },
    [windows, nextZIndex, calculateNewWindowPosition]
  );

  /**
   * Close a window
   */
  const closeWindow = useCallback((windowId: string) => {
    setWindows((prev) => {
      const filtered = prev.filter((w) => w.id !== windowId);

      if (filtered.length === 0) {
        return filtered;
      }

      const closedWindow = prev.find((w) => w.id === windowId);
      if (!closedWindow?.isFocused) {
        return filtered;
      }

      // Focus the window with the highest z-index among the remaining ones
      const highestZ = Math.max(...filtered.map((w) => w.zIndex));

      return filtered.map((w) => ({
        ...w,
        isFocused: w.zIndex === highestZ,
        isMinimized: w.zIndex === highestZ ? false : w.isMinimized,
      }));
    });
  }, []);

  /**
   * Focus a window (bring to front)
   */
  const focusWindow = useCallback(
    (windowId: string) => {
      setWindows((prev) =>
        prev.map((w) => ({
          ...w,
          isFocused: w.id === windowId,
          zIndex: w.id === windowId ? nextZIndex : w.zIndex,
          isMinimized: w.id === windowId ? false : w.isMinimized,
        }))
      );
      setNextZIndex((z) => z + 1);
    },
    [nextZIndex]
  );

  /**
   * Minimize a window
   */
  const minimizeWindow = useCallback((windowId: string) => {
    setWindows((prev) =>
      prev.map((w) =>
        w.id === windowId
          ? { ...w, isMinimized: true, isFocused: false }
          : w
      )
    );
  }, []);

  /**
   * Restore a minimized window
   */
  const restoreWindow = useCallback(
    (windowId: string) => {
      focusWindow(windowId); // Restoring also focuses the window
    },
    [focusWindow]
  );

  /**
   * Update window position
   */
  const updateWindowPosition = useCallback(
    (windowId: string, position: { x: number; y: number }) => {
      setWindows((prev) =>
        prev.map((w) =>
          w.id === windowId ? { ...w, position } : w
        )
      );
    },
    []
  );

  /**
   * Update window size
   */
  const updateWindowSize = useCallback(
    (windowId: string, size: { width: number; height: number }) => {
      setWindows((prev) =>
        prev.map((w) =>
          w.id === windowId ? { ...w, size } : w
        )
      );
    },
    []
  );

  /**
   * Close all windows
   */
  const closeAllWindows = useCallback(() => {
    setWindows([]);
  }, []);

  const cycleWindows = useCallback(
    (direction: 'next' | 'prev') => {
      if (windows.length === 0) {
        return;
      }

      const ordered = windows;
      const focusedIndex = ordered.findIndex((w) => w.isFocused);
      let nextIndex = 0;

      if (focusedIndex === -1) {
        nextIndex = direction === 'next' ? 0 : ordered.length - 1;
      } else if (direction === 'next') {
        nextIndex = (focusedIndex + 1) % ordered.length;
      } else {
        nextIndex = (focusedIndex - 1 + ordered.length) % ordered.length;
      }

      focusWindow(ordered[nextIndex].id);
    },
    [windows, focusWindow]
  );

  useEffect(() => {
    if (windows.length === 0) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const isModifierPressed = event.metaKey || event.ctrlKey;
      if (!isModifierPressed) {
        return;
      }

      const key = event.key.toLowerCase();
      const focusedWindow = windows.find((w) => w.isFocused) ?? windows[windows.length - 1];

      if (key === 'w') {
        event.preventDefault();
        if (focusedWindow) {
          closeWindow(focusedWindow.id);
        }
        return;
      }

      if (event.key === 'Tab') {
        event.preventDefault();
        cycleWindows(event.shiftKey ? 'prev' : 'next');
        return;
      }

      if (key === 'm') {
        event.preventDefault();
        if (focusedWindow) {
          minimizeWindow(focusedWindow.id);
        }
        return;
      }

      if (/^[1-5]$/.test(event.key)) {
        event.preventDefault();
        const index = Number(event.key) - 1;
        const targetWindow = windows[index];
        if (targetWindow) {
          focusWindow(targetWindow.id);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [windows, closeWindow, cycleWindows, minimizeWindow, focusWindow]);

  const value: WindowManagerContextValue = {
    windows,
    openWindow,
    closeWindow,
    focusWindow,
    minimizeWindow,
    restoreWindow,
    updateWindowPosition,
    updateWindowSize,
    closeAllWindows,
  };

  return (
    <WindowManagerContext.Provider value={value}>
      {children}
    </WindowManagerContext.Provider>
  );
}

/**
 * Hook to use WindowManager
 */
export function useWindowManager() {
  const context = useContext(WindowManagerContext);
  if (!context) {
    throw new Error('useWindowManager must be used within WindowManagerProvider');
  }
  return context;
}
